import { useAuthStore } from '@/store/auth-store';
import { useCommonCodeStore } from '@/store/commoncode-store';
import { useLoadingStore } from '@/store/loading-store';
import { useNotificationStore } from '@/store/notification-store';

import GlobalComponents from '@/plugins/global-components';
import { useRouterStore } from '@/store/router-store';
import '@/style/override.css';
import { loadScript } from '@/utils/scriptLoader';
import { definePreset } from '@primevue/themes';
import Aura from '@primevue/themes/aura';
import { createPinia } from 'pinia';
import piniaPersistedstate from 'pinia-plugin-persistedstate';
import 'primeicons/primeicons.css';
import PrimeVue from 'primevue/config';
import ConfirmationService from 'primevue/confirmationservice';
import ToastService from 'primevue/toastservice';
import { createApp } from 'vue';
import App from './App.vue';
import router, { addDynamicRoutes } from './router';

import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';

import '@/assets/styles.scss';
import '@/assets/tailwind.css';
const app = createApp(App);

const pinia = createPinia();
pinia.use(piniaPersistedstate);
app.use(pinia);
app.use(GlobalComponents);
app.use(router);

async function initKakaoMap() {
    try {
        await loadScript('//t1.daumcdn.net/mapjsapi/bundle/postcode/prod/postcode.v2.js');

        await new Promise((resolve) => setTimeout(resolve, 100));

        // console.log('카카오 우편번호 API 로딩 완료');
        // console.log('window.daum 사용 가능:', !!window.daum);
        // console.log('window.daum.Postcode 사용 가능:', !!(window.daum && window.daum.Postcode));

        return window.daum;
    } catch (error) {
        console.error('kakao Postcode Api 초기화 실패:', error);
        return null;
    }
}

//authStore.initializeAuth();

const MyPreset = definePreset(Aura, {
    colors: {
        indigo: {
            50: '#E6ECFE',
            100: '#C2D1FD',
            200: '#9EB6FC',
            300: '#7A9BFB',
            400: '#5680FA',
            500: '#0038EA', // 여기에 원하는 기본 색상을 설정
            600: '#002DC2',
            700: '#00229A',
            800: '#001672',
            900: '#000B4A',
            950: '#000524'
        }
    },
    semantic: {
        primary: {
            50: '{indigo.50}',
            100: '{indigo.100}',
            200: '{indigo.200}',
            300: '{indigo.300}',
            400: '{indigo.400}',
            500: '{indigo.500}',
            600: '{indigo.600}',
            700: '{indigo.700}',
            800: '{indigo.800}',
            900: '{indigo.900}',
            950: '{indigo.950}'
        }
    }
});

async function initializeApp() {
    const authStore = useAuthStore();
    const routerStore = useRouterStore();
    const commonCodeStore = useCommonCodeStore();
    const loadingStore = useLoadingStore();
    const notificationStore = useNotificationStore();

    app.use(PrimeVue, {
        theme: {
            preset: MyPreset,
            options: {
                darkModeSelector: '.app-dark'
            }
        },
        locale: {
            firstDayOfWeek: 0,
            dayNames: ['일요일', '월요일', '화요일', '수요일', '목요일', '금요일', '토요일'],
            dayNamesShort: ['일', '월', '화', '수', '목', '금', '토'],
            dayNamesMin: ['일', '월', '화', '수', '목', '금', '토'],
            monthNames: ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월'],
            monthNamesShort: ['1월', '2월', '3월', '4월', '5월', '6월', '7월', '8월', '9월', '10월', '11월', '12월']
            // today: '오늘',
            // clear: '초기화'
        }
    });
    app.use(ToastService);
    app.use(ConfirmationService);

    try {
        await authStore.checkAuth();
    } catch (error) {
        console.error('Authentication Error', error);
        await router.push('/login');
        return;
    }

    //await routerStore.fetchRouterItems(); // 라우터 정보 최신화
    await addDynamicRoutes(router, routerStore.routerList);
    await router.isReady();
    notificationStore.initializeWebSocket();

    await Promise.all([
        commonCodeStore.fetchCommonCodes(),
        initKakaoMap().then((kakaoMapInstance) => {
            if (kakaoMapInstance) {
                app.provide('kakaoMapInstance', kakaoMapInstance);
            }
        })
    ]);

    router.beforeEach((to, from, next) => {
        loadingStore.showLoading(); // 로딩 시작
        next();
    });

    router.afterEach(() => {
        loadingStore.hideLoading(); // 로딩 종료
    });

    const requestedPath = window.location.pathname;
    const routes = router.getRoutes();
    const matchedRoute = routes.find((route) => route.path === requestedPath);

    if (requestedPath !== '/' && matchedRoute) {
        const query = {};
        const searchParams = new URLSearchParams(window.location.search);
        for (const [key, value] of searchParams.entries()) {
            query[key] = value;
        }

        await router.replace({
            path: requestedPath,
            query: query
        });
    } else {
        await router.replace('/');
    }

    app.mount('#app');
}

initializeApp().catch((error) => {
    console.error('Failed to initialize app:', error);

    const appLoader = document.getElementById('app-loader');
    if (appLoader) {
        appLoader.innerHTML = `
            <div style="text-align: center; font-family: sans-serif;">
                <h2>Error occurred</h2>
                <p>A problem occurred while starting the app.</p>
            </div>
        `;
    }
});
